<!-- Prompt Reuse Card — shown when PromptEnhancerMiddleware emits a PROMPT_REUSE_SUGGESTION interrupt -->
<div class="reuse-card">

    <!-- ── Header ──────────────────────────────────────────────────────────── -->
    <div class="reuse-card__header theme-bg-light">
        <!-- Lightbulb icon badge -->
        <span class="reuse-card__header-icon-wrap">
            <app-icon type="lightbulb" source="lucide" size="sm" />
        </span>

        <!-- Title + subtitle -->
        <span class="reuse-card__header-body">
            <span class="reuse-card__header-title">
                Matched {{ candidates().length }} saved prompt{{ candidates().length !== 1 ? 's' : '' }}
            </span>
            <span class="reuse-card__header-sub">
                Relevant prompts were found in your library. Reuse, edit, or create new
            </span>
        </span>

        <!-- Text-based countdown — replaces the circular ring -->
        <div class="reuse-card__timer-text flex items-baseline bg-white rounded-base px-2 py-1 gap-1"
            [class.reuse-card__timer-text--urgent]="timeRemaining() <= 10">
            <span class="text-xs">Auto start in</span>
            <span class="text-sm">{{ formatTime(timeRemaining()) }}</span>
        </div>
    </div>

    <!-- ── Candidate list ──────────────────────────────────────────────────── -->
    <div class="reuse-card__list p-3 space-y-3">
        @for (candidate of candidates(); track candidate.promptId; let i = $index) {
        <div class="reuse-card__item border rounded-base"
            [class.reuse-card__item--loading]="loadingPromptId() === candidate.promptId">

            <div class="flex flex-col gap-3 p-3 pb-0">
                <!-- Row 1: badges (left) + match % pill (right) -->
                <div class="reuse-card__item-header">
                    <div class="reuse-card__badges">
                        @if (isTopPick(i)) {
                        <!-- Highlighted gradient badge for the highest-ranked candidate -->
                        <span class="reuse-card__top-pick"><app-icon type="star" source="lucide" size="xs" /> Top
                            pick</span>
                        }

                        <!-- Scope badge: team (WORKSPACE) → orange + users icon; personal → border + user icon -->
                        @if (candidate.accessLevel === AccessLevel.WORKSPACE) {
                        <div class="reuse-card__scope-badge reuse-card__scope-badge--team" pTooltip="Workspace"
                            tooltipPosition="top">
                            <app-icon type="users" source="lucide" size="xs" variant="black" />
                        </div>
                        } @else {
                        <div class="reuse-card__scope-badge reuse-card__scope-badge--personal" pTooltip="Personal"
                            tooltipPosition="top">
                            <app-icon type="user" source="lucide" size="xs" variant="black" />
                        </div>
                        }

                        <!-- Group badge — folder icon; tooltip lists all group names; hidden when no groups -->
                        @if (groupsLabel(candidate)) {
                        <div class="reuse-card__scope-badge reuse-card__scope-badge--group"
                            [pTooltip]="groupsLabel(candidate)" tooltipPosition="top">
                            <app-icon type="folder" source="lucide" size="xs" variant="black" />
                        </div>
                        }
                    </div>

                    <!--
                    Match percentage pill — colour tier driven by scoreColor():
                      green  > 80 % | yellow 50–80 % | gray < 50 %
                -->
                    <span class="reuse-card__match-pill"
                        [class.reuse-card__match-pill--high]="scorePercent(candidate.score) > 80"
                        [class.reuse-card__match-pill--mid]="scorePercent(candidate.score) >= 50 && scorePercent(candidate.score) <= 80"
                        [class.reuse-card__match-pill--low]="scorePercent(candidate.score) < 50">
                        {{ scorePercent(candidate.score) }}% match
                    </span>
                </div>

                <!-- Row 2: prompt title -->
                <div class="text-lg font-bold line-clamp-2">{{ candidate.title }}</div>
                <div class="flex flex-col gap-0">
                    <!-- Row 3: expand / collapse toggle — chevron trails the label on the right -->
                    <app-button
                        [label]="expandedPromptId() === candidate.promptId ? 'Hide prompt structure guide' : 'Show prompt structure guide'"
                        variant="ghost" size="xxs" class="text-xs"
                        [icon]="expandedPromptId() === candidate.promptId ? 'chevron-up' : 'chevron-down'"
                        iconPosition="right" [iconSource]="'lucide'" (clicked)="toggleExpand(candidate.promptId)" />

                    @if (expandedPromptId() === candidate.promptId) {
                    <div class="reuse-card__preview">
                        <pre class="reuse-card__preview-text">{{ candidate.content }}</pre>
                    </div>
                    }
                </div>
            </div>

            <!-- Row 4: creator + run count (left) | Use prompt button (right) -->
            <div class="reuse-card__item-footer border-t p-3">
                <div class="reuse-card__meta">
                    @if (candidate.creator) {
                    <app-user-name-display [user]="creatorAsUser(candidate.creator)" variant="avatar" size="xs" />
                    <span class="reuse-card__meta-sep">·</span>
                    }
                    @if (candidate.timesUsed) {
                    <app-icon type="play" source="lucide" size="xs" class="reuse-card__meta-icon" />
                    <span class="reuse-card__meta-runs">
                        {{ candidate.timesUsed }} run{{ candidate.timesUsed !== 1 ? 's' : '' }}
                    </span>
                    }
                </div>
                <!-- Primary CTA:
                     - Top pick (i === 0): always visible
                     - Others: fades in on item hover, or while loading -->
                <div class="reuse-card__use-btn" [class.reuse-card__use-btn--always]="i === 0"
                    [class.reuse-card__use-btn--visible]="loadingPromptId() === candidate.promptId">
                    <app-button label="Use prompt" size="sm" severity="primary" variant="filled"
                        [loading]="loadingPromptId() === candidate.promptId"
                        [disabled]="loadingPromptId() !== null || isGenerating()"
                        (clicked)="onUseExisting(candidate.promptId)" />
                </div>
            </div>

        </div>
        }
    </div>

    <!-- ── Footer ──────────────────────────────────────────────────────────── -->
    <div class="reuse-card__footer">
        <!-- Generate: proceed with normal AI enhancement instead of reusing -->
        <app-button label="Generate new prompt" severity="primary" variant="outlined" size="sm" [icon]="'sparkles'"
            [iconSource]="'lucide'" [loading]="isGenerating()" [disabled]="loadingPromptId() !== null || isGenerating()"
            (clicked)="onGenerateNew()" />
    </div>

</div>
